// assets/xgen-facets.js
import { selectedFacets } from 'xgen-state';
import { refreshFacetResults } from 'xgen-api';

const SELECTORS = {
  CONTAINER: '#xgen-filters',
  FILTER_GROUP: '.xgen-filter-group',
  FILTER_HEADER: '.xgen-filters-header',
  FILTER_BODY: '.xgen-filters-body',
  BODY_WRAPPER: '.xgen-filters--body-wrapper',
  CHECKBOX_ITEM: '.xgen-filters-checkbox-item',
  MORE_BTN: '.xgen-filters-more-button',
  PRICE_GO_BTN: '#xgen-price-submit-btn',
  PRICE_INPUT_FROM: '#xgen-price-range-from',
  PRICE_INPUT_TO: '#xgen-price-range-to',
  PRICE_CONTAINER: '.price-range-container',
  HIDDEN_OPTION: '.hidden-option'
};

const CLASSES = {
  EXPANDED: 'xgen-filters-body--expanded',
  SELECTED: 'selected'
};

// Constructs the filter context object needed for API calls.
export function buildFilterContext(selected) {
  const context = {};
  const arrayKeys = new Set(['colors', 'availability', 'brand', 'model']);
  for (const [key, val] of Object.entries(selected)) {
    if (!val) continue;

    // 1. Handle (Special Object Case)
    if (key === 'price' && !Array.isArray(val)) {
      if (val.min) context.min = parseFloat(val.min);
      if (val.max) context.max = parseFloat(val.max);
      continue;
    }

    // 2. Handle All Arrays
    if (Array.isArray(val) && val.length > 0) {
      // If it's in our "keep as array" list, assign directly. Otherwise, join it.
      context[key] = arrayKeys.has(key) ? val : val.join(' ');
    }
  }

  return context;
}
export function displayFacets(res) {
  const facetsArray = Array.isArray(res) ? res : res?.facets;
  if (!facetsArray) return;
  const hasKey = ['brand', 'model', 'type', 'price', 'availability'];
  const keysToSkip = ['rating_count', 'rating_value', 'colors'];
  // const valuesToSkip = ['-', 'Discontinued', 'No Backorders'];
  const valuesToSkip = ['-'];
  const formattedFacets = facetsArray.reduce((acc, item) => {
    if (!item.facets) return acc;
    Object.entries(item.facets).forEach(([categoryName, facetValues]) => {
      const isAllowed = hasKey.includes(categoryName);
      const isBlacklisted = keysToSkip.includes(categoryName);
      if (!isAllowed || isBlacklisted) return;
      if (!acc[categoryName]) acc[categoryName] = {};

      Object.entries(facetValues).forEach(([valKey, rawCount]) => {
        if (valuesToSkip.includes(valKey)) return;
        
        const count = parseInt(rawCount, 10);
        if (!isNaN(count) && count > 0) {
          acc[categoryName][valKey] = count; 
        }
      });

      if (Object.keys(acc[categoryName]).length === 0) {
        delete acc[categoryName];
      }
    });
    return acc;
  }, {});
  renderFacets(formattedFacets);
}

function renderFacets(facets) {
  const container = document.querySelector(SELECTORS.CONTAINER);
  if (!container) return;
  attachGlobalFacetListeners(container);
  const fragment = document.createDocumentFragment();
  const customOrder = ['brand', 'model', 'type', 'price', 'availability'];
  const sortedFacets = Object.entries(facets).sort(([keyA], [keyB]) => {
    let indexA = customOrder.indexOf(keyA.toLowerCase());
    let indexB = customOrder.indexOf(keyB.toLowerCase());
    if (indexA === -1) indexA = 99;
    if (indexB === -1) indexB = 99;
    return indexA - indexB;
  });
  
  // Object.entries(facets).forEach(([facetName, options]) => {
  sortedFacets.forEach(([facetName, options]) => {
    const isPrice = facetName.toLowerCase() === 'price';
    const section = document.createElement('div');
    section.className = 'xgen-filter-group';
    section.id = `xgen-filters-${facetName}`;
    const header = document.createElement('div');
    header.className = 'xgen-filters-header';
    header.setAttribute('role', 'button');
    // const isInitiallyExpanded = window.innerWidth > 768;
    const isInitiallyExpanded = true;
    header.setAttribute('aria-expanded', String(isInitiallyExpanded));
    header.innerHTML = `
      <label class="heading heading--5 margin-0">${formatFacetTitle(facetName)}</label>
      <svg class="icon icon--chevron-down" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" height="24" width="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"><path d="m6 9 6 6 6-6"></path></svg>
    `;
    const body = document.createElement('div');
    body.id = `xgen-filters-${facetName}-body`;
    body.className = `xgen-filters-body${isInitiallyExpanded ? ' ' + CLASSES.EXPANDED : ''}`;
    const bodyWrapper = document.createElement('div');
    bodyWrapper.className = 'xgen-filters--body-wrapper';
    bodyWrapper.dataset.filterName = facetName;

    let entries;
    if (isPrice) {
      entries = getPriceRangeEntries(options);
    } else {
      entries = Object.entries(options);
    }

    entries.forEach(([optionLabel, count, priceMin, priceMax], index) => {
      const optionDiv = document.createElement('div');
      optionDiv.className = 'xgen-filters-checkbox-item';
      optionDiv.dataset.name = facetName;
      optionDiv.dataset.type = isPrice ? 'range' : 'text';
      optionDiv.setAttribute('tabindex', '0');
      optionDiv.setAttribute('role', 'checkbox');
      optionDiv.setAttribute('aria-checked', 'false');

      if (isPrice) {
        const min = priceMin ?? 0;
        const max = priceMax ?? '';
        optionDiv.id = `xgen-filter-${min}-${max || 'max'}`;
        optionDiv.dataset.minPrice = min;
        optionDiv.dataset.maxPrice = max;
      } else {
        optionDiv.id = `xgen-filter-${optionLabel.replace(/\s+/g, '-')}`;
        optionDiv.setAttribute('value', optionLabel);
      }

      if (!isPrice && index >= 5) {
        optionDiv.style.display = 'none';
        optionDiv.classList.add('hidden-option');
      }

      optionDiv.innerHTML = `
        <label class="xgen-filters-checkbox-item-value"><span>${optionLabel}</span></label>
        <span class="xgen-filters-checkbox-item-count paragraph-2">(${count})</span>
      `;
      bodyWrapper.appendChild(optionDiv);
    });

    if (isPrice && selectedFacets.price) {
    const { min, max } = selectedFacets.price;
    const filterId = `xgen-filter-${min}-${max || 'max'}`;
    if (!bodyWrapper.querySelector(`#${filterId}`)) {
        const customDiv = document.createElement('div');
        customDiv.className = `${CLASSES.SELECTED} xgen-filters-checkbox-item`;
        customDiv.dataset.name = 'price';
        customDiv.dataset.type = 'range';
        customDiv.dataset.minPrice = min;
        customDiv.dataset.maxPrice = max || '';
        customDiv.id = filterId;
        customDiv.setAttribute('aria-checked', 'true');
        
        const label = max ? `$${parseFloat(min)} - $${parseFloat(max)}` : `$${parseFloat(min)} & up`;
        customDiv.innerHTML = `<label class="xgen-filters-checkbox-item-value"><span>${label}</span></label>
        <span class="xgen-filters-checkbox-item-count paragraph-2 custom-price-count"></span>`;
        const priceContainer = bodyWrapper.querySelector(SELECTORS.PRICE_CONTAINER);
        bodyWrapper.insertBefore(customDiv, priceContainer);
    }
}

    // "More" button
    if (!isPrice && entries.length > 5) {
      const toggleBtn = document.createElement('button');
      toggleBtn.className = 'xgen-filters-more-button small-text';
      toggleBtn.textContent = '+ More';
      bodyWrapper.appendChild(toggleBtn);
    }

    // Custom Price Inputs
    if (isPrice) {
      renderPriceInputs(bodyWrapper, options);
    }

    body.appendChild(bodyWrapper);
    section.appendChild(header);
    section.appendChild(body);
    fragment.appendChild(section);
  });

  // Single DOM update: append the complete fragment
  container.innerHTML = '';
  container.appendChild(fragment);
}

// Calculation of price ranges. Iterates through data once. Returns raw min/max to avoid regex later.
function getPriceRangeEntries(priceData) {
  const ranges = [
    { label: 'under $100', min: 0, max: 100, count: 0 },
    { label: '$100 - $200', min: 100, max: 200, count: 0 },
    { label: '$200 - $300', min: 200, max: 300, count: 0 },
    { label: '$300 - $400', min: 300, max: 400, count: 0 },
    { label: '$400 - $500', min: 400, max: 500, count: 0 },
    { label: '$500 - $1,000', min: 500, max: 1000, count: 0 },
    { label: '$1,000 & up', min: 1000, max: Infinity, count: 0 }
  ];

  for (const [priceStr, qty] of Object.entries(priceData)) {
    const price = parseFloat(priceStr);
    if (isNaN(price)) continue;
    for (const r of ranges) {
      if (price >= r.min && price < r.max) {
        r.count += qty;
        break;
      }
    }
  }

  return ranges
    .filter(r => r.count > 0)
    .map(r => [r.label, r.count, r.min, r.max === Infinity ? null : r.max]);
}

// Custom price input boxes.
function renderPriceInputs(container, priceOptions) {
  const prices = Object.keys(priceOptions).map(parseFloat).filter(n => !isNaN(n));
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const maxPrice = prices.length ? Math.max(...prices) : 0;
  const priceInputHtml = `
      <div class="price-range-container">
        <div class="price-range-content">
          <div class="xgen-price-range-input-container text-box">
            <label><span>$</span><input min="${minPrice}" max="${maxPrice}" type="number" class="price-range-input" id="${SELECTORS.PRICE_INPUT_FROM.substring(1)}"></label>
          </div>
          <span>-</span>
          <div class="xgen-price-range-input-container text-box">
            <label>$<input min="${minPrice}" max="${maxPrice}" type="number" class="price-range-input" id="${SELECTORS.PRICE_INPUT_TO.substring(1)}"></label>
          </div>
          <button id="${SELECTORS.PRICE_GO_BTN.substring(1)}" class="button-text">Go</button>
        </div>
      </div>
    `;
  container.insertAdjacentHTML('beforeend', priceInputHtml);
}

// Event Handling to click on facet.
function attachGlobalFacetListeners(container) {
  if (container.dataset.eventsAttached === 'true') return;
  container.dataset.eventsAttached = 'true';
  container.addEventListener('click', (e) => {
    const target = e.target;

    // Handle Facet Checkbox Click
    const checkboxItem = target.closest(SELECTORS.CHECKBOX_ITEM);
    if (checkboxItem) {
      handleFacetItemClick(checkboxItem);
      return;
    }

    // Handle Header Collapse/Expand Click
    const header = target.closest(SELECTORS.FILTER_HEADER);
    if (header) {
      toggleGroupExpand(header);
      return;
    }

    // Handle "More/Less" Button Click
    const moreBtn = target.closest(SELECTORS.MORE_BTN);
    if (moreBtn) {
      toggleShowMore(moreBtn);
      return;
    }

    // Handle Price "Go" Button Click
    const goBtn = target.closest(SELECTORS.PRICE_GO_BTN);
    if (goBtn) {
      handleCustomPriceSubmit(goBtn);
      return;
    }
  });

  // Handle keyboard navigation (Enter/Space on checkboxes)
  container.addEventListener('keypress', (e) => {
    const checkboxItem = e.target.closest(SELECTORS.CHECKBOX_ITEM);
    if (checkboxItem && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleFacetItemClick(checkboxItem);
    }
  });
}

async function handleFacetItemClick(item) {
  const { type: facetType, name: facetName, minPrice, maxPrice } = item.dataset;
  const value = item.getAttribute('value');
  const isSelected = item.getAttribute('aria-checked') === 'true';
  const newState = !isSelected;
  item.setAttribute('aria-checked', String(newState));
  item.classList.toggle(CLASSES.SELECTED, newState);
  if (facetType === 'range' && facetName === 'price') {
    if (newState) {
      document.querySelectorAll(`${SELECTORS.CHECKBOX_ITEM}[data-name="price"]`).forEach(el => {
        if (el !== item) {
          el.setAttribute('aria-checked', 'false');
          el.classList.remove(CLASSES.SELECTED);
        }
      });
      selectedFacets[facetName] = { min: minPrice, max: maxPrice };
    } else {
      delete selectedFacets[facetName];
    }
  } else {
    if (!selectedFacets[facetName]) selectedFacets[facetName] = [];
    if (newState) {
      if (!selectedFacets[facetName].includes(value)) selectedFacets[facetName].push(value);
    } else {
      selectedFacets[facetName] = selectedFacets[facetName].filter(v => v !== value);
      if (selectedFacets[facetName].length === 0) delete selectedFacets[facetName];
    }
  }
  await updateUrlAndRefresh(selectedFacets);
}

function toggleGroupExpand(header) {
  const body = header.nextElementSibling;
  const isExpanded = header.getAttribute('aria-expanded') === 'true';
  header.setAttribute('aria-expanded', String(!isExpanded));
  body.classList.toggle(CLASSES.EXPANDED, !isExpanded);
}

function toggleShowMore(btn) {
  const wrapper = btn.closest(SELECTORS.BODY_WRAPPER);
  const hiddenOptions = wrapper.querySelectorAll(SELECTORS.HIDDEN_OPTION);
  const isExpanded = btn.textContent.includes('- Less');
  hiddenOptions.forEach(opt => {
    opt.style.display = isExpanded ? 'none' : 'flex';
  });
  btn.textContent = isExpanded ? '+ More' : '- Less';
}
function handleCustomPriceSubmit(btn) {
  const wrapper = btn.closest(SELECTORS.BODY_WRAPPER);
  const fromInput = wrapper.querySelector(SELECTORS.PRICE_INPUT_FROM);
  const toInput = wrapper.querySelector(SELECTORS.PRICE_INPUT_TO);
  const min = fromInput.value !== '' ? parseFloat(fromInput.value) : 0;
  const max = toInput.value !== '' ? parseFloat(toInput.value) : null;
  if ((fromInput.value === '' && toInput.value === '') || (max !== null && min > max)) {
    return;
  }
  // selectedFacets['price'] = { min: min.toString(), max: max ? max.toString() : '' };
  // updateUrlAndRefresh(selectedFacets);

  const filterId = `xgen-filter-${min}-${max ?? 'max'}`;
  let targetOption = wrapper.querySelector(`#${filterId}`);
  if (!targetOption) {
    const optionLabel = max !== null 
      ? `$${min} - $${max}` 
      : `$${min} & up`;

    targetOption = document.createElement('div');
    targetOption.className = 'xgen-filters-checkbox-item';
    targetOption.dataset.name = 'price';
    targetOption.dataset.type = 'range';
    targetOption.dataset.minPrice = min;
    targetOption.dataset.maxPrice = max || '';
    targetOption.setAttribute('tabIndex', 0);
    targetOption.setAttribute('role', 'checkbox');
    targetOption.setAttribute('aria-checked', 'false');
    targetOption.id = filterId;
    targetOption.innerHTML = `<label class="xgen-filters-checkbox-item-value"><span>${optionLabel}</span></label><span class="xgen-filters-checkbox-item-count paragraph-2 custom-price-count"></span>`;
    const priceContainer = wrapper.querySelector(SELECTORS.PRICE_CONTAINER);
    wrapper.insertBefore(targetOption, priceContainer);
  }
  if (typeof handleFacetItemClick === 'function') {
    handleFacetItemClick(targetOption);
  }

  // Reset inputs
  fromInput.value = '';
  toInput.value = '';
}

// Updates the URL based on selected facets and triggers an API refresh.
async function updateUrlAndRefresh(selected) {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Clear existing filter params
  for (const key of [...params.keys()]) {
    if (key.startsWith('filter_') && key !== 'filter_sort') params.delete(key);
    if (['min_price', 'max_price', 'page'].includes(key)) params.delete(key);
  }

  // Rebuild params from state
  Object.entries(selected).forEach(([facet, values]) => {
    if (!values) return;
    if (facet === 'price' && !Array.isArray(values)) {
      if (values.min) params.set('min_price', values.min);
      if (values.max) params.set('max_price', values.max);
    } else if (Array.isArray(values) && values.length > 0) {
      params.append(`filter_${facet}`, values.map(v => encodeURIComponent(v)).join(','));
    }
  });

  window.history.replaceState({}, '', url.toString());
  await refreshFacetResults(params, selected);
}

// Restoration Functions (Sync UI with URL/State after render)
export function restoreSelectedFacets(selected) {
  const facets = selected || window.selectedFacets;
  if (!facets) return;

  Object.entries(facets).forEach(([facetName, values]) => {
    if (!values) return;

    if (facetName === 'price' && !Array.isArray(values)) {
      const minStr = values.min || '0';
      const maxStr = values.max || '';
      markOptionSelected(`.xgen-filters-checkbox-item[data-name="price"][data-min-price="${minStr}"][data-max-price="${maxStr}"]`);

    } else if (Array.isArray(values)) {
      values.forEach(value => {
        markOptionSelected(`.xgen-filters-checkbox-item[data-name="${facetName}"][value="${CSS.escape(value)}"]`);
      });
    }
  });
}

export function restoreFacetSelections() {
  const params = new URLSearchParams(window.location.search);

  for (const [key, value] of params.entries()) {
    if (key.startsWith('filter_') && key !== 'filter_sort') {
      const facetName = key.replace('filter_', '');
      decodeURIComponent(value).split(',').forEach(v => {
        markOptionSelected(`.xgen-filters-checkbox-item[data-name="${facetName}"][value="${CSS.escape(decodeURIComponent(v))}"]`);
      });
    }
  }

  const minStr = params.get('min_price');
  const maxStr = params.get('max_price');
  if (minStr !== null || maxStr !== null) {
    const min = minStr || '0';
    const max = maxStr || '';
    markOptionSelected(`.xgen-filters-checkbox-item[data-name="price"][data-min-price="${min}"][data-max-price="${max}"]`);
  }
}

function markOptionSelected(selector) {
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute('aria-checked', 'true');
    el.classList.add(CLASSES.SELECTED);
  }
}

function formatFacetTitle(name) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}